import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { useDiscoContent } from "hooks/useDiscoContent";

import { ImmersiveReaderButton } from "components/ImmersiveReaderButton/ImmersiveReaderButton";
import { LoadingSpinner } from "components/LoadingSpinner";
import { EdifySubject } from "./components/EdifySubject/EdifySubject";

import { InfoMessage } from "components/InfoMessage/InfoMessage";
import { useTranslation } from "react-i18next";
import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssFlex } from "styles/helpers/layout";

export interface PathParams {
  productId: string;
  packageId: string;
}

const IR_TARGET_SELECTOR = "subject-list-immersive-reader";

export const EdifyModuleDetailsPage: React.FC = observer(() => {
  const { t } = useTranslation("homepage");
  const { packageId } = useParams() as unknown as PathParams;
  const { loading: isLoading, fetchEdifyModuleDetails, edifyModuleDetails } = useDiscoContent();

  useEffect(() => {
    if (packageId) {
      fetchEdifyModuleDetails(packageId);
    }
  }, [packageId, fetchEdifyModuleDetails]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!edifyModuleDetails) {
    return (
      <InfoMessage
        dataCy="no-content-message"
        icon="fas fa-folder-open"
        title={t("edifyModules.noContentError.title")}
      >
        <ParagraphLarge marginBottom={5}>
          {t("edifyModules.noContentError.description", { packageId })}
        </ParagraphLarge>
      </InfoMessage>
    );
  }

  return (
    <CssFlex className={IR_TARGET_SELECTOR} flexDirection="column" rowGap={2}>
      <Heading2 data-cy="edify-module-title" marginTop="0.5rem">
        <ImmersiveReaderButton
          targetIndex={0}
          targetSelector={`.${IR_TARGET_SELECTOR}`}
          trackingValue={{ module: STUDENT_MODULES.BASIC_PROGRESS }}
        />
        {edifyModuleDetails.title}
      </Heading2>
      <section data-cy="edify-module-subject-list">
        {edifyModuleDetails.subjects.map((subject) => (
          <EdifySubject key={subject.id} subject={subject} />
        ))}
      </section>
    </CssFlex>
  );
});
